/* eslint-disable camelcase -- Firestore field names are the persisted Turkish schema. */
import { timestampMillis } from './league-offer-week';
import { photoRatio } from './feed-post';

// Akışın canlılık yardımcıları (28 Ekim 2026): yeni gelişme denetimi, bugünkü maçlar, fotoğraf oranı.

const ISTANBUL_OFFSET = 3 * 60 * 60 * 1000;
const DAY = 24 * 60 * 60 * 1000;
// Rezerve maç saatinden sonra bu süre "şu an oynuyor" sayılır; sonrası sonuç bekler.
export const LIVE_MATCH_MS = 2 * 60 * 60 * 1000;
// Açık sekmede yeni gelişme denetimi aralığı (tek belgelik okuma, CDN önbellekli).
export const FEED_LATEST_POLL_MS = 60 * 1000;
export const TODAY_MATCH_LIMIT = 12;

export function istanbulDay(now: number) {
  const start = Math.floor((now + ISTANBUL_OFFSET) / DAY) * DAY - ISTANBUL_OFFSET;
  return { start, end: start + DAY };
}

export type TodayMatchState = 'upcoming' | 'live' | 'awaiting' | 'entered';

export function todayMatchState(at: number, status: number, now: number): TodayMatchState {
  if (status === 4) return 'entered';
  if (now < at) return 'upcoming';
  return now < at + LIVE_MATCH_MS ? 'live' : 'awaiting';
}

/** İstanbul takvimine göre bugün saati olan rezerve (1) ya da sonucu girilmiş (4) tekler maçları. */
export function pickTodayMatches<T extends { mac_durum?: unknown; mac_saati?: unknown }>(
  matches: T[],
  now: number,
  limit = TODAY_MATCH_LIMIT,
): { match: T; at: number }[] {
  const { start, end } = istanbulDay(now);
  return matches
    .filter(match => match.mac_durum === 1 || match.mac_durum === 4)
    .map(match => ({ match, at: timestampMillis(match.mac_saati) }))
    .filter(item => item.at >= start && item.at < end)
    .sort((a, b) => a.at - b.at)
    .slice(0, limit);
}

export const newestFeedTime = (events: { olusturma: number }[]) =>
  events.reduce((max, event) => Math.max(max, Number(event.olusturma) || 0), 0);

/** İlk sayfada, ekrandaki en yeni olaydan sonra gelen ve gizlenmemiş olayların kimlikleri. */
export function newFeedEventIds(
  page: { id: string; olusturma: number }[],
  current: { id: string; olusturma: number }[],
  hidden: Set<string> = new Set(),
): string[] {
  const known = new Set(current.map(event => event.id));
  const newest = newestFeedTime(current);
  return page
    .filter(event => !known.has(event.id) && !hidden.has(event.id) && event.olusturma > newest)
    .map(event => event.id);
}

const u16 = (image: Uint8Array, at: number, little = false) =>
  little ? image[at] + image[at + 1] * 256 : image[at] * 256 + image[at + 1];
const u32 = (image: Uint8Array, at: number, little: boolean) =>
  little
    ? u16(image, at, true) + u16(image, at + 2, true) * 65536
    : u16(image, at) * 65536 + u16(image, at + 2);

/** APP1 Exif bölümündeki yön etiketi (0x0112); yoksa null. */
function exifOrientation(image: Uint8Array, start: number, end: number): number | null {
  const header = [0x45, 0x78, 0x69, 0x66, 0, 0];
  if (end - start < 14 || header.some((byte, index) => image[start + index] !== byte)) return null;
  const tiff = start + 6;
  const little = image[tiff] === 0x49 && image[tiff + 1] === 0x49;
  if (!little && !(image[tiff] === 0x4d && image[tiff + 1] === 0x4d)) return null;
  const ifd = tiff + u32(image, tiff + 4, little);
  if (ifd + 2 > end) return null;
  const count = u16(image, ifd, little);
  for (let index = 0; index < count; index++) {
    const entry = ifd + 2 + index * 12;
    if (entry + 12 > end) return null;
    if (u16(image, entry, little) === 0x0112) return u16(image, entry + 8, little);
  }
  return null;
}

/**
 * JPEG'in görüntülenen boyutu (SOF başlığı; Exif yönü 5–8 ise en/boy yer değiştirir).
 * Tarayıcılar Exif yönünü uyguladığı için oran ekranda görüneni anlatır. Bozuk dosyada null.
 */
export function jpegSize(image: Uint8Array): { width: number; height: number } | null {
  if (image[0] !== 0xff || image[1] !== 0xd8) return null;
  let offset = 2;
  let orientation = 1;
  while (offset + 4 <= image.length) {
    if (image[offset] !== 0xff) return null;
    const marker = image[offset + 1];
    if (marker === 0xff) {
      offset += 1;
    } else {
      if (marker === 0xd9 || marker === 0xda) return null;
      const length = u16(image, offset + 2);
      if (length < 2 || offset + 2 + length > image.length) return null;
      if (marker === 0xe1) {
        orientation = exifOrientation(image, offset + 4, offset + 2 + length) || orientation;
      }
      if (marker >= 0xc0 && marker <= 0xcf && ![0xc4, 0xc8, 0xcc].includes(marker)) {
        const height = u16(image, offset + 5);
        const width = u16(image, offset + 7);
        if (!width || !height) return null;
        return orientation >= 5 && orientation <= 8
          ? { width: height, height: width }
          : { width, height };
      }
      offset += 2 + length;
    }
  }
  return null;
}

export function jpegRatio(image: Uint8Array): number | undefined {
  const size = jpegSize(image);
  return size ? photoRatio(size.width / size.height) : undefined;
}

/**
 * B1: "İyi şanslar" yalnız bugün (İstanbul), henüz başlamamış rezerve maça; destekleyen aynı ligin
 * üyesi ve maçın oyuncusu değil. Sunucu (web API) ve arayüz aynı kuralı kullanır.
 */
export function canCheerMatch(
  match: { mac_durum?: unknown; mac_saati?: unknown; lig_temel_id?: unknown; kul_t_id?: unknown; kul_r_id?: unknown },
  supporter: { id?: string | null; lig_temel_id?: string | null },
  now: number,
): boolean {
  if (match.mac_durum !== 1 || !supporter.id || supporter.lig_temel_id !== match.lig_temel_id) return false;
  if ([match.kul_t_id, match.kul_r_id].includes(supporter.id)) return false;
  const at = timestampMillis(match.mac_saati);
  const { start, end } = istanbulDay(now);
  return at >= start && at < end && now < at;
}
